import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, DialogFooter, DialogHeader } from '@qodo/design-system';
import { FEATURE_FLAGS } from 'consts/features';
import { CONTACT_SUPPORT_URL } from 'consts/url';
import { useAccountBilling } from 'hooks/billing/useAccountBilling';
import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { useConversionPopup } from 'hooks/billing/useConversionPopup';
import { useCreateCheckoutSession } from 'hooks/billing/useCreateCheckoutSession';
import { usePaidMoveFlow } from 'hooks/billing/usePaidMoveFlow';
import { usePricingConfig } from 'hooks/billing/usePricingConfig';
import { useRecommendation } from 'hooks/billing/useRecommendation';
import { useSelfServeGate } from 'hooks/billing/useSelfServeGate';
import { useShowsReversedMigrationOffer } from 'hooks/billing/useShowsReversedMigrationOffer';
import { useFeatureAccess } from 'hooks/useFeatureAccess';
import { ArrowRight, X } from 'lucide-react';
import { SUBSCRIPTION_STATE } from 'network/httpClients/BillingClient/api.schema';
import { useBillingInfoQuery } from 'network/queries/useBillingInfoQuery';
import { useBillingQuotaBalanceQuery } from 'network/queries/useBillingQuotaBalanceQuery';
import { ROUTES } from 'routes/consts';
import { mixpanelService } from 'services/mixpanel';
import {
  CONVERSION_POPUP_DISMISS_METHODS,
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { type ConversionPopupDismissMethod } from 'services/mixpanel/types';
import { buildSubscriptionContext } from 'services/mixpanel/utils';
import { captureBillingError } from 'services/sentry/captureBillingError';
import { CONVERSION_COHORTS } from 'types/billing';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'ui/dialog';
import {
  clearCheckoutSku,
  rememberCheckoutSku,
} from 'utils/billing/checkoutReturn';
import {
  getMigrationDaysRemaining,
  getMigrationWindowDays,
  isMigratedFreeUser,
  isMigratedPaidUser,
  isMovePendingReversal,
} from 'utils/billing/conversion';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { openUrl } from 'utils/url';
import {
  DowngradeConfirmationDialog,
  type DowngradeTarget,
} from 'components/DowngradeConfirmationDialog';
import {
  UpgradeConfirmationDialog,
  type UpgradeTarget,
} from 'components/UpgradeConfirmationDialog';
import { PaymentActionConsent } from '../parts/PaymentActionConsent';
import { TrialBonusAlert } from '../parts/TrialBonusAlert';
import {
  CONTACT_SALES_URL,
  ENTERPRISE_FEATURE_KEYS,
  type PlanTier,
} from './consts';
import { CheckoutConfirmation } from './parts/CheckoutConfirmation';
import { FeatureList } from './parts/FeatureList';
import { PlanGroupCard } from './parts/PlanGroupCard';
import { SkuSelector } from './parts/SkuSelector';
import { TrialBadge } from './parts/TrialBadge';
import {
  getCheckoutErrorKey,
  getHasExistingSubscription,
  resolveCurrentPlanCredits,
} from './utils';

const spawnPopup = spawn('billing.popup');

/**
 * Global plan-selection popup, matched to the "Find the plan that fits your team"
 * Figma. Two selectable tiers (Team Pro / Enterprise); the footer CTA is
 * "Continue to checkout" for Team Pro and swaps to "Talk to sales" for Enterprise.
 *
 * Team Pro checkout (PLT-3715): persists the selected SKU, creates a Stripe
 * Checkout session via POST /billing/v1/checkout/session, and redirects to the
 * hosted URL. On return, the popup is reopened in a confirmation view
 * (`confirmation` set by usePostCheckoutReturn) — see CheckoutConfirmation.
 */
export const ConversionPopup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, selectedSku, triggerSource, mode, confirmation, close } =
    useConversionPopup();
  const cfg = usePricingConfig();
  const { data: accountInfo } = useAccountInfo();
  const { isTrialLapsed } = useAccountBilling();
  const { mutateAsync: createCheckoutSession, isPending: isCheckoutPending } =
    useCreateCheckoutSession();
  // Paid Move + its 3DS reversal recovery: a silent success closes the popup, a
  // card that needs auth flips `paymentActionNeeded` (→ inline consent step).
  const paidMove = usePaidMoveFlow({ onMoveComplete: close });
  // Reversed-but-not-yet-expired users get the same migration day-badge + discount
  // ladder as pre-cancel users (consumed in migrationDaysRemaining below).
  const showsReversedOffer = useShowsReversedMigrationOffer();
  // Enterprise users never see this popup; non-admins see it but can't buy.
  const { isSelfServe, canPurchase } = useSelfServeGate();
  const isMigratedPaid = isMigratedPaidUser(accountInfo);
  // Show the reversal consent when the Move just parked (local, immediate) OR the
  // account is already parked on the server (a reload mid-flow) — so a 3DS-failed
  // user is never stranded and can always resume. After they agree, the account
  // becomes self-serve and this popup falls through to the normal plan selection.
  const showMoveConsent =
    paidMove.paymentActionNeeded || isMovePendingReversal(accountInfo);
  // Paid migration users have selfServe=false but canManageBilling=true, so they
  // pass the render guard and get the checkout CTA.
  const canCheckout = canPurchase || isMigratedPaid;
  const {
    isEnabled: isDowngradeFlagEnabled,
    isLoading: isDowngradeFlagLoading,
  } = useFeatureAccess({
    featureId: FEATURE_FLAGS.SELF_SERVE_DOWNGRADE_ENABLED,
  });
  const { isEnabled: isUpgradeFlagEnabled, isLoading: isUpgradeFlagLoading } =
    useFeatureAccess({
      featureId: FEATURE_FLAGS.SELF_SERVE_UPGRADE_ENABLED,
    });

  const { data: recommendation } = useRecommendation();
  // /billing-info is not fetched app-wide, so pull it only while the popup is
  // open to enrich the PLT-4031 funnel events with subscription context.
  const {
    data: billingInfo,
    isPending: isBillingInfoPending,
    isError: isBillingInfoError,
    isRefetching: isBillingInfoRefetching,
    refetch: refetchBillingInfo,
  } = useBillingInfoQuery({ enabled: isOpen });
  // No existing subscription = a fresh purchase: the standard checkout path,
  // never gated. ACTIVE, PAST_DUE, SCHEDULED_TO_CANCEL, and SCHEDULED_TO_CHANGE
  // are all existing subscribers (pastDue is a live plan in dunning grace), so
  // none is a fresh checkout. Routing a non-downgrade selection to Stripe would
  // let an existing subscriber re-purchase what they already own. Keyed on actual
  // subscription state (planSku can be null/off-ladder yet still active), not
  // carousel membership.
  //
  // A lapsed trial is the exception: the Stripe trial shell is still `trialing`,
  // which the BE maps to 'active', so this reads as an existing subscriber for an
  // account that never purchased. Left unhandled it marks the shell's pack as
  // "current" and blocks the CTA as a plan change — locking the user out of the
  // very checkout this popup exists to offer them.
  const hasExistingSubscription =
    getHasExistingSubscription(billingInfo?.subscriptionState) &&
    !isTrialLapsed;
  const showCheckoutPopup = !hasExistingSubscription;
  // Change-plan only means something while a plan is actually live. Once the
  // subscription is canceled the caller's latched mode is stale, so the popup
  // falls back to a plain purchase — otherwise the dead pack would still be
  // marked as "current" and every other pack read as an up/downgrade of it.
  const isChangePlanMode = mode === 'change-plan' && hasExistingSubscription;
  // Period-end date the scheduled downgrade takes effect. Only needed
  // in change-plan mode; fetched while the popup is open, same as billing-info.
  const { data: quotaBalance } = useBillingQuotaBalanceQuery({
    enabled: isOpen && isChangePlanMode,
  });
  const downgradeEffectiveAt =
    quotaBalance?.credits?.renewalDate ?? quotaBalance?.cycleEnd ?? null;
  const subscriptionInfo = useMemo(
    () => buildSubscriptionContext(billingInfo),
    [billingInfo],
  );

  // Usage-recommended SKU → gets the badge + pre-select. The BE ladder is a
  // superset of the popup's carousel, so an exact credit match isn't guaranteed;
  // pick the smallest SKU whose pool covers the recommended credits (the BE's own
  // "closest SKU above" rule). Enterprise handoff arrives with recommendedPlan: null,
  // so this memo returns undefined in that case. If BE sends an out-of-range plan
  // without handoff, clamp to the highest carousel SKU.
  const recommendedSku = useMemo<string | undefined>(() => {
    if (!recommendation?.available || !recommendation.recommendedPlan) {
      return undefined;
    }

    const target = recommendation.recommendedPlan.credits;
    const ascending = [...cfg.plans].sort((a, b) => a.credits - b.credits);
    const covering = ascending.find((plan) => plan.credits >= target);

    return (covering ?? ascending[ascending.length - 1])?.sku;
  }, [cfg.plans, recommendation]);

  // Default selection when there's no recommendation: the smallest rung.
  // Derived from the live ladder since SKUs are no longer a fixed set (PLT-4162).
  const defaultSku = useMemo<string>(() => {
    const ascending = [...cfg.plans].sort((a, b) => a.credits - b.credits);

    return ascending[0]?.sku ?? '';
  }, [cfg.plans]);

  // The user's current plan rung, when it maps to a live ladder SKU. Drives the
  // default pre-selection and the "current plan" marker. planSku is null for
  // trial/free users and may sit outside the carousel, so callers fall through
  // to the recommendation / default when this is undefined. A canceled account
  // keeps its last planSku on the wire, so the live-subscription check is what
  // stops a dead pack from being marked (and locked) as the current plan.
  const currentPlanSku = useMemo<string | undefined>(() => {
    const planSku = billingInfo?.planSku;

    if (!planSku || !hasExistingSubscription) {
      return undefined;
    }

    return cfg.plans.find((plan) => plan.sku === planSku)?.sku;
  }, [billingInfo?.planSku, hasExistingSubscription, cfg.plans]);

  const [tier, setTier] = useState<PlanTier>('team-pro');
  const [activeSku, setActiveSku] = useState<string>(selectedSku ?? defaultSku);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [downgradeTarget, setDowngradeTarget] =
    useState<DowngradeTarget | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<UpgradeTarget | null>(
    null,
  );

  // Seed the selection once per open, not on every recommendation resolution:
  // keying the reset on recommendedSku re-fires when the async recommendation
  // lands while the dialog is open and clobbers an in-progress choice. An
  // enterprise-handoff recommendation seeds the Enterprise tier (PLT-4059).
  const wasOpen = useRef(false);

  useEffect(() => {
    // Wait for billing-info to settle before the one-shot seed so the current
    // plan can win the pre-selection instead of being clobbered by the default
    // when planSku arrives late.
    if (isOpen && !wasOpen.current && !isBillingInfoPending) {
      wasOpen.current = true;
      setTier(recommendation?.enterpriseHandoff ? 'enterprise' : 'team-pro');
      setActiveSku(
        selectedSku ?? currentPlanSku ?? recommendedSku ?? defaultSku,
      );
      setCheckoutError(null);
    } else if (!isOpen) {
      wasOpen.current = false;
    }
  }, [
    isOpen,
    isBillingInfoPending,
    selectedSku,
    currentPlanSku,
    recommendedSku,
    recommendation,
    defaultSku,
  ]);

  // Fire CONVERSION_POPUP_VIEWED once per plan-selection open (PLT-4031). The
  // confirmation view reuses the same dialog but is a different funnel step
  // (CHECKOUT_RETURN_SUCCESS), so skip it here. The ref resets on close so a
  // re-open re-fires, but StrictMode's double-invoke / re-renders don't.
  const viewTrackedRef = useRef(false);

  // Set by the specific Radix dismiss callbacks (esc / outside-click / close-X)
  // so the single onOpenChange-close path can report how the popup was
  // dismissed (PLT-4031).
  const dismissMethodRef = useRef<ConversionPopupDismissMethod | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!isOpen) {
      viewTrackedRef.current = false;

      return;
    }

    // Hold the VIEW until /billing-info has settled (resolved OR errored) so the
    // event carries subscription_info. `isPending` flips false on error too, so
    // a failed/blocked billing-info never suppresses the funnel-entry event.
    if (confirmation || viewTrackedRef.current || isBillingInfoPending) {
      return;
    }

    viewTrackedRef.current = true;
    const trial = accountInfo?.trial;

    mixpanelService.track(MIXPANEL_EVENT_NAMES.VIEW, {
      operation: MIXPANEL_OPERATIONS.CONVERSION_POPUP_VIEWED,
      interaction_details: {
        trigger_source: triggerSource,
        trial_day_remaining: trial?.active ? trial.daysRemaining : undefined,
        subscription_info: subscriptionInfo,
      },
    });
  }, [
    isOpen,
    confirmation,
    triggerSource,
    accountInfo,
    subscriptionInfo,
    isBillingInfoPending,
  ]);

  // The live ladder loads async; if it arrives (or changes) while the popup is
  // open and the current selection isn't a real rung, re-align so the carousel
  // always has a valid, checkout-able SKU selected.
  useEffect(() => {
    if (!isOpen || !cfg.plans.length) {
      return;
    }

    if (!cfg.plans.some((plan) => plan.sku === activeSku)) {
      setActiveSku(currentPlanSku ?? recommendedSku ?? defaultSku);
    }
  }, [
    isOpen,
    cfg.plans,
    activeSku,
    currentPlanSku,
    recommendedSku,
    defaultSku,
  ]);

  const onSelectSku = (sku: string) => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.PLAN_CHECKBOX_CLICKED,
      interaction_details: { plan_sku: sku },
    });
    setActiveSku(sku);
    setTier('team-pro');
  };

  const trialInfo =
    accountInfo?.tier !== 'paid' ? accountInfo?.trial : undefined;
  const isMigratedFree = isMigratedFreeUser(accountInfo);
  const isMigratedMonthly =
    isMigratedPaid &&
    accountInfo?.conversionCohort === CONVERSION_COHORTS.monthly;
  const migrationTitleKey = isMigratedPaid
    ? isMigratedMonthly
      ? 'title-migrated'
      : 'title-migration'
    : isMigratedFree
      ? 'title-migrated'
      : 'title';
  const titleKey = isChangePlanMode ? 'change-plan.title' : migrationTitleKey;
  // Pre-cancel (awaiting move) and reversed-but-not-yet-expired users both get the
  // migration day-badge + discount ladder; the reversed side hides once it expires
  // OR once they buy (the hook folds in the "no active subscription" check).
  const migrationDaysRemaining =
    isMigratedPaid || showsReversedOffer
      ? getMigrationDaysRemaining(accountInfo?.migrationEndDate)
      : undefined;
  const migrationWindowDays = getMigrationWindowDays(
    accountInfo?.migrationStartDate,
    accountInfo?.migrationEndDate,
  );
  const migrationTrialInfo =
    migrationDaysRemaining !== undefined && migrationDaysRemaining > 0
      ? {
          active: true,
          daysRemaining: migrationDaysRemaining,
          dayNumber: Math.min(
            migrationWindowDays,
            Math.max(1, migrationWindowDays - migrationDaysRemaining + 1),
          ),
          totalDays: migrationWindowDays,
        }
      : undefined;
  const selectedPlan = cfg.plans.find((plan) => plan.sku === activeSku);
  const currentPlan = cfg.plans.find((plan) => plan.sku === currentPlanSku);
  const isEnterprise = tier === 'enterprise';
  // The current credit count, resolved independent of carousel membership so an
  // off-ladder current plan still compares correctly. Stays undefined without a
  // live subscription so no selection classifies as an up/downgrade of a plan
  // the account no longer owns.
  const currentPlanCredits = hasExistingSubscription
    ? resolveCurrentPlanCredits(currentPlan, billingInfo?.planSku)
    : undefined;
  // A downgrade is a change-plan-mode selection of a pack strictly smaller than
  // the current one — it schedules at period end rather than charging now.
  const isDowngradeSelection =
    isChangePlanMode &&
    !isEnterprise &&
    !!selectedPlan &&
    currentPlanCredits !== undefined &&
    selectedPlan.credits < currentPlanCredits;
  // An upgrade is the mirror: a strictly larger pack. It applies immediately with
  // a prorated charge and routes to the upgrade confirmation dialog, gated behind
  // is_self_serve_upgrade_enabled (flag off → CTA disabled "coming soon").
  const isUpgradeSelection =
    isChangePlanMode &&
    !isEnterprise &&
    !!selectedPlan &&
    currentPlanCredits !== undefined &&
    selectedPlan.credits > currentPlanCredits;

  // Billing-info must resolve *successfully* before any gating: while it's
  // pending/errored it's undefined (reads as "no active plan"), so an existing
  // subscriber would flash the wrong CTA on a cold cache / slow network — and on
  // an outright error could fall through to a fresh Stripe purchase they already
  // own. `isPending` flips false on error too, so the error is excluded
  // explicitly.
  const isBillingInfoReady =
    !isBillingInfoPending && !isBillingInfoError && billingInfo !== undefined;
  // A terminal billing-info error leaves the CTA disabled (isCtaReady stays
  // false) — surface it with a retry so the popup isn't a silent dead end during
  // a transient backend/network failure.
  const showBillingInfoError = isBillingInfoError && !billingInfo;
  // Only the *selected* direction's flag gates the CTA. Waiting on both would let
  // a hung upgrade-flag request block an otherwise-ready downgrade (and vice
  // versa), since a selection is only ever one direction. A neutral selection
  // (nothing / same plan) touches neither flag and is blocked regardless, so it
  // needn't wait on either.
  const isRelevantFlagLoading =
    (isDowngradeSelection && isDowngradeFlagLoading) ||
    (isUpgradeSelection && isUpgradeFlagLoading);
  const areFlagsReady = !isRelevantFlagLoading;
  const isScheduledToCancel =
    billingInfo?.subscriptionState === SUBSCRIPTION_STATE.SCHEDULED_TO_CANCEL;
  // Change-plan gating (block "coming soon" / route to a dialog) is only
  // trustworthy once billing-info AND the selected direction's flag settle. A
  // fresh checkout doesn't depend on the flags, so it isn't held here (see
  // isCtaReady).
  const isGatingReady = isBillingInfoReady && areFlagsReady;
  // An existing subscriber's enabled actions are a flag-enabled downgrade or a
  // flag-enabled upgrade; anything else (either direction with its flag off) is
  // "coming soon". Only assert this once gating is ready, so the flag-off branch
  // never fires transiently while billing-info / the flags are still loading.
  const isEnabledDowngrade = isDowngradeFlagEnabled && isDowngradeSelection;
  const isEnabledUpgrade = isUpgradeFlagEnabled && isUpgradeSelection;
  // The migrated-paid CTA is the "Move" (or its reversal consent) — a distinct
  // server action, NOT a self-serve pack change — so it must never be gated by the
  // self-serve upgrade/downgrade flags, even though the account's legacy sub reads
  // as an "existing subscription". Reversed users have no sub, so they're a fresh
  // checkout and never reach this branch.
  const blockChangePlan =
    isGatingReady &&
    !showCheckoutPopup &&
    !isMigratedPaid &&
    !isEnabledDowngrade &&
    !isEnabledUpgrade;
  // The CTA needs billing-info to know which path it's on. A fresh checkout is
  // safe as soon as billing-info says there's no existing subscription — it never
  // touches the self-serve flags — so only make it wait on the flags for an
  // existing subscriber (where flag state decides block vs route).
  const isCtaReady = isBillingInfoReady && (showCheckoutPopup || areFlagsReady);
  // Either confirmation dialog being up suppresses the popup so no two Radix
  // modals stack.
  const isConfirmationDialogOpen =
    downgradeTarget !== null || upgradeTarget !== null;
  // "Coming soon" tooltip on the blocked CTA — an upgrade-specific key when the
  // blocked selection is an upgrade, else the generic copy.
  const comingSoonTooltip = !blockChangePlan
    ? undefined
    : isUpgradeSelection
      ? spawnPopup('change-plan.upgrade-coming-soon')
      : t('general.coming-soon');
  // Three-way CTA: downgrade / upgrade / fresh checkout. Migration users get
  // "Move now" instead of "Continue to checkout".
  const ctaLabelKey = isDowngradeSelection
    ? 'change-plan.cta'
    : isUpgradeSelection
      ? 'change-plan.upgrade-cta'
      : isMigratedPaid
        ? 'footer.move-now'
        : 'footer.checkout';

  const onCtaClick = async () => {
    if (isEnterprise) {
      mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
        operation: MIXPANEL_OPERATIONS.CONVERSION_POPUP_LARGER_TEAMS_CLICKED,
        interaction_details: { subscription_info: subscriptionInfo },
      });
      window.open(CONTACT_SALES_URL, '_blank', 'noopener,noreferrer');

      return;
    }

    // Ladder still loading / unavailable — never POST an empty or stale SKU
    // against the live Stripe ladder (PLT-4162). The CTA is disabled in this
    // window too; this guards the handler itself.
    if (!selectedPlan) {
      return;
    }

    // Downgrade: route to the confirmation dialog (scheduled at period end, no
    // immediate charge) instead of Stripe Checkout. effectiveAt comes from the
    // quota period end; without it we can't show an accurate date, so bail.
    if (isDowngradeSelection) {
      if (!downgradeEffectiveAt) {
        return;
      }

      setDowngradeTarget({
        targetProductId: selectedPlan.sku,
        credits: selectedPlan.credits,
        effectiveAt: downgradeEffectiveAt,
      });

      return;
    }

    // Upgrade: route to the confirmation dialog (immediate prorated charge via
    // change-plan) instead of Stripe Checkout — never a fresh checkout for a plan
    // the subscriber already owns. When the flag is off the CTA is disabled, so
    // this early return is the belt-and-suspenders guard against a checkout leak.
    if (isUpgradeSelection) {
      if (isUpgradeFlagEnabled) {
        setUpgradeTarget({
          targetProductId: selectedPlan.sku,
          credits: selectedPlan.credits,
        });
      }

      return;
    }

    // Paid-migration "Move now" → the server-side Move (swap on the card on file),
    // NOT self-serve checkout: a paid-migration account is self_serve:false by
    // design, so checkout's gate 403s. The flow closes the popup on a silent
    // success, renders the inline consent step if the card needs auth (3DS), or
    // surfaces `paidMove.moveFailed`. Mirrors MigrationWelcomePopup's handling.
    if (isMigratedPaid) {
      mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
        operation: MIXPANEL_OPERATIONS.CONVERSION_POPUP_CHECKOUT_CLICKED,
        interaction_details: {
          plan_sku: activeSku,
          subscription_info: subscriptionInfo,
        },
      });

      paidMove.onMove(activeSku);

      return;
    }

    setCheckoutError(null);
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.CONVERSION_POPUP_CHECKOUT_CLICKED,
      interaction_details: {
        plan_sku: activeSku,
        subscription_info: subscriptionInfo,
      },
    });
    // Stash the SKU before leaving — the Stripe return URL only carries the
    // session id, so the confirmation reads the SKU back from here.
    rememberCheckoutSku(activeSku);

    try {
      const { url } = await createCheckoutSession(activeSku);

      // Same-tab navigation to the Stripe-hosted Checkout page.
      openUrl(url);
    } catch (error) {
      captureBillingError(error, 'checkout');
      clearCheckoutSku();
      setCheckoutError(spawnPopup(getCheckoutErrorKey(error)));
    }
  };

  // The post-checkout confirmation close is not a funnel drop-off, so it's excluded.
  const onDialogClose = () => {
    if (!confirmation) {
      mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
        operation: MIXPANEL_OPERATIONS.CONVERSION_POPUP_DISMISSED,
        interaction_details: {
          dismiss_method: dismissMethodRef.current,
          subscription_info: subscriptionInfo,
        },
      });
    }

    dismissMethodRef.current = undefined;
    paidMove.reset();
    close();
  };

  // Enterprise accounts can't self-serve purchase — never render the popup
  // (they also have no entry point, this is the belt-and-suspenders guard).
  // While the paid Move is parked awaiting the user's reversal consent, the
  // account is momentarily neither self-serve nor `awaiting_move` (the record is
  // `payment_action_required`). Keep rendering so the inline consent step the user
  // is mid-way through survives the account-info refetch that the Move triggers.
  if (!isSelfServe && !isMigratedPaid && !showMoveConsent) {
    console.warn(
      'Attempted to open ConversionPopup for non-self-serve account; aborting render',
    );

    return <></>;
  }

  return (
    <>
      {/* Suppress the popup while a confirmation dialog (downgrade or upgrade) is
        up so the two Radix modals never stack. The programmatic hide must NOT run
        the dismiss path (it would fire a spurious CONVERSION_POPUP_DISMISSED +
        close the popup); cancelling a confirmation dialog clears its target and
        the popup returns. */}
      <Dialog
        open={isOpen && !isConfirmationDialogOpen}
        onOpenChange={(open) =>
          !open && !isConfirmationDialogOpen && onDialogClose()
        }
      >
        {/* Zero the DS slide-in translate so this wide modal zoom+fades in
          centered instead of flying in from the top-left (PLT-4153). */}
        <DialogContent
          className={cn(
            'flex max-h-[90vh] flex-col gap-0 slide-in-from-right-[0px]! slide-in-from-bottom-[0px]! slide-out-to-right-[0px]! slide-out-to-bottom-[0px]! bg-bg-fill-panel-neutral!',
            confirmation ? 'max-w-sm p-0' : 'max-w-240 p-10',
          )}
          onEscapeKeyDown={() => {
            dismissMethodRef.current = CONVERSION_POPUP_DISMISS_METHODS.ESC;
          }}
          // Background clicks must not dismiss the popup — the user leaves only
          // via the close button, Esc, or a plan action.
          onPointerDownOutside={(event) => {
            event.preventDefault();
          }}
        >
          {confirmation ? (
            <CheckoutConfirmation confirmation={confirmation} />
          ) : (
            <>
              <DialogHeader className="flex-row items-start justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <TrialBadge
                    trial={migrationTrialInfo ?? trialInfo}
                    activeKey={
                      migrationTrialInfo
                        ? 'migration-progress-badge'
                        : undefined
                    }
                  />
                </div>
                <DialogClose
                  aria-label={spawnPopup('close-aria')}
                  onClick={() => {
                    dismissMethodRef.current =
                      CONVERSION_POPUP_DISMISS_METHODS.CLOSE_X;
                  }}
                  className="shrink-0 text-text-panel-neutral-mid transition-colors hover:text-text-panel-neutral-strong"
                >
                  <X className="size-5" />
                </DialogClose>
              </DialogHeader>
              <div className="mt-6 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
                <DialogTitle className="text-2xl font-normal text-text-general-neutral-strong font-inter">
                  {spawnPopup(titleKey)}
                </DialogTitle>
                <div
                  role="radiogroup"
                  aria-label={spawnPopup('group-aria-label')}
                  className="flex flex-col gap-6"
                >
                  <PlanGroupCard
                    tier="team-pro"
                    name={spawnPopup('group.team-pro.name')}
                    audience={spawnPopup('group.team-pro.audience')}
                    checked={tier === 'team-pro'}
                    collapsed
                    onSelect={setTier}
                  >
                    <div className="flex flex-col gap-8">
                      {/* The ladder loads async from Stripe (PLT-4162). Until it
                          arrives, show a loading line instead of a $0 price and
                          an empty, un-buyable carousel. */}
                      {cfg.isPlansReady ? (
                        <>
                          <div className="flex flex-col">
                            <span className="text-xs text-text-panel-neutral-strong tracking-[0.12px]">
                              {spawnPopup('group.team-pro.audience-plus')}
                            </span>
                            <span className="text-3xl font-normal text-text-panel-neutral-strong">
                              {spawnPopup('price-monthly', {
                                price: String(selectedPlan?.priceMonthly ?? 0),
                              })}
                            </span>
                          </div>
                          <SkuSelector
                            cfg={cfg}
                            // Clear the SKU highlight when Enterprise is the
                            // active tier — selecting Enterprise removes the Team
                            // Pro selection (Dana). Clicking a SKU flips back to
                            // Team Pro.
                            activeSku={isEnterprise ? undefined : activeSku}
                            recommendedSku={recommendedSku}
                            currentPlanSku={currentPlanSku}
                            onSelectSku={onSelectSku}
                          />
                        </>
                      ) : (
                        <span className="text-sm text-text-panel-neutral-mid">
                          {spawnPopup('loading-plans')}
                        </span>
                      )}
                    </div>
                    <FeatureList />
                    {/* During the reversal-consent step the only action is the
                        consent block below — suppress the Move CTA banner so it
                        doesn't compete with it. */}
                    {!showMoveConsent && (
                      <TrialBonusAlert
                        onCtaClick={onCtaClick}
                        variant="popup"
                      />
                    )}
                  </PlanGroupCard>

                  <PlanGroupCard
                    tier="enterprise"
                    name={spawnPopup('group.enterprise.name')}
                    audience={spawnPopup('group.enterprise.audience')}
                    checked={tier === 'enterprise'}
                    collapsed
                    onSelect={setTier}
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-text-panel-neutral-strong tracking-[0.12px]">
                        {spawnPopup('group.enterprise.audience-plus')}
                      </span>
                      <span className="text-3xl font-normal text-text-panel-neutral-strong">
                        {spawnPopup('group.enterprise.headline')}
                      </span>
                    </div>
                    <FeatureList
                      featureKeys={ENTERPRISE_FEATURE_KEYS}
                      featureNamespace="enterprise-feature"
                      intro={spawnPopup('whats-included')}
                    />
                  </PlanGroupCard>
                </div>
                <p className="text-sm text-text-general-neutral-subtle">
                  {spawnPopup('overage-note.prefix')}{' '}
                  <Button
                    onClick={() => {
                      close();
                      navigate(ROUTES.BILLING_MANAGE_SUBSCRIPTION);
                    }}
                    variant="link"
                    className="px-1 py-0 h-5 underline"
                  >
                    {spawnPopup('overage-note.settings-link')}
                  </Button>
                </p>
                <p className="text-sm text-text-general-neutral-subtle">
                  <Trans
                    i18nKey="billing.popup.support"
                    components={{
                      supportLink: (
                        <Button
                          onClick={() => {
                            mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
                              operation:
                                MIXPANEL_OPERATIONS.PLAN_CHOOSER_CONTACT_SUPPORT_CLICKED,
                            });
                            openUrl(CONTACT_SUPPORT_URL, true);
                          }}
                          variant="link"
                          className="px-1 py-0 h-5 underline"
                        />
                      ),
                    }}
                  />
                </p>
              </div>

              {showMoveConsent && (
                <PaymentActionConsent
                  onConfirm={paidMove.onConfirmReverse}
                  loading={paidMove.isBusy}
                />
              )}

              <DialogFooter className="h-12 mt-6 items-center justify-between gap-4">
                <span className="text-xs text-text-general-neutral-mild font-inter">
                  {spawnPopup('footer.terms')}
                </span>
                <div className="flex flex-col items-end gap-2">
                  {checkoutError && (
                    <span className="text-sm text-destructive">
                      {checkoutError}
                    </span>
                  )}
                  {paidMove.moveFailed && (
                    <span className="text-sm text-destructive">
                      {spawnPopup('move-error')}
                    </span>
                  )}
                  {showMoveConsent ? null : showBillingInfoError ? (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-destructive">
                        {spawnPopup('billing-info-error')}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        loading={isBillingInfoRefetching}
                        onClick={() => refetchBillingInfo()}
                      >
                        {spawnPopup('billing-info-error-retry')}
                      </Button>
                    </div>
                  ) : isEnterprise ? (
                    <Button onClick={onCtaClick} className="gap-1.5">
                      {spawnPopup('footer.sales')}
                    </Button>
                  ) : canCheckout ? (
                    <Button
                      onClick={onCtaClick}
                      loading={isCheckoutPending || paidMove.isBusy}
                      disabled={
                        !isCtaReady ||
                        blockChangePlan ||
                        isCheckoutPending ||
                        paidMove.isBusy ||
                        showMoveConsent ||
                        !selectedPlan ||
                        (isDowngradeSelection && !downgradeEffectiveAt)
                      }
                      tooltip={comingSoonTooltip}
                      className="gap-1.5"
                      size="sm"
                    >
                      <ArrowRight className="size-4" />
                      {spawnPopup(ctaLabelKey)}
                    </Button>
                  ) : (
                    <span className="text-sm text-text-panel-neutral-mid">
                      {spawnPopup('admin-required')}
                    </span>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DowngradeConfirmationDialog
        open={downgradeTarget !== null}
        onOpenChange={(open) => !open && setDowngradeTarget(null)}
        target={downgradeTarget}
        isScheduledToCancel={isScheduledToCancel}
        onConfirm={close}
      />

      <UpgradeConfirmationDialog
        open={upgradeTarget !== null}
        onOpenChange={(open) => !open && setUpgradeTarget(null)}
        target={upgradeTarget}
        isScheduledToCancel={isScheduledToCancel}
        onConfirm={close}
      />
    </>
  );
};
